import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface Props {
  title: string;
  children: ReactNode;
  delay?: number;
}

// Tooltip rules of engagement:
//
// 1. The wrapper span uses `display: contents` — it generates NO box
//    of its own. The child button/element is rendered by the parent
//    layout exactly as if the wrapper were not there. Zero risk of
//    the wrapper's styles cascading to or overlaying the trigger.
//
// 2. The pill is rendered via createPortal into document.body, with
//    `position: fixed`. It is outside every parent's layout, so no
//    overflow:hidden or stacking context can clip it. z-index 10000
//    keeps it above modals and dropdowns.
//
// 3. The pill is positioned against the FIRST real child element's
//    bounding rect (not the wrapper itself — a display:contents span
//    has no rect). 8px gap above the trigger, transform-centered.
//
// 4. `pointer-events: none` on the pill means it can never intercept
//    a click intended for the trigger.
//
// Touch devices suppress the pill entirely (hover: none).
function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

export function Tooltip({ title, children, delay = 200 }: Props) {
  const [placement, setPlacement] = useState<{
    x: number;
    y: number;
    below: boolean;
  } | null>(null);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const timerRef = useRef<number | null>(null);
  // Once the user clicks inside the wrapper, suppress re-show until
  // the pointer actually leaves and re-enters. Without this, hovering
  // over a button-that-opens-a-menu immediately pops the tooltip back
  // up on top of the menu.
  const suppressedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  function show() {
    if (isTouchDevice()) return;
    if (suppressedRef.current) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const wrapper = wrapperRef.current;
      // display: contents wrapper has no box — reach through to the
      // first rendered child for measurement.
      const target =
        (wrapper?.firstElementChild as HTMLElement | null) ?? wrapper;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const below = rect.top < 40;
      setPlacement({
        x: rect.left + rect.width / 2,
        y: below ? rect.bottom : rect.top,
        below,
      });
    }, delay);
  }

  function hide() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPlacement(null);
  }

  // Click inside the wrapper: dismiss the pill and suppress further
  // pops until the pointer leaves. The dropdown/menu the click is
  // probably opening sits at the same screen region and the tooltip
  // would otherwise overlap it.
  function onPointerDown() {
    hide();
    suppressedRef.current = true;
  }

  // Mouse leaves the wrapper → lift the suppression so the next
  // hover shows the tooltip as usual.
  function onMouseLeave() {
    hide();
    suppressedRef.current = false;
  }

  // Hide on scroll/resize so the pill doesn't drift from its trigger.
  useEffect(() => {
    if (!placement) return;
    const onHide = () => hide();
    window.addEventListener("scroll", onHide, true);
    window.addEventListener("resize", onHide);
    return () => {
      window.removeEventListener("scroll", onHide, true);
      window.removeEventListener("resize", onHide);
    };
  }, [placement]);

  const portalTarget =
    typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <span
        ref={wrapperRef}
        style={{ display: "contents" }}
        onMouseEnter={show}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {placement && portalTarget
        ? createPortal(
            <div
              role="tooltip"
              className="ui-tooltip-pill"
              style={{
                position: "fixed",
                top: placement.y,
                left: placement.x,
                transform: placement.below
                  ? "translate(-50%, 8px)"
                  : "translate(-50%, calc(-100% - 8px))",
                pointerEvents: "none",
                zIndex: 10000,
              }}
            >
              {title}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
}

export default Tooltip;
